import React, { useEffect, useState } from "react";
import { UseAuthToken } from "../../../../../../libs/api/userSession";
import useSWR from "swr";
import {
  parseClientError,
  swrFetcher,
} from "../../../../../../libs/api/Endpoints";
import s from "./ViewUsage.module.scss";
import clsx from "clsx";
import NoData from "../../../../../../global components/Table/NoData/NoData";
import Unauthorised from "../../../../../../global components/Unauthorised";
import ReusableTable from "../../../../../../global components/Table/Table";
import Paginate from "../../../../../../global components/Paginate/Pagination";
import SelectField from "../../../../../../global components/Forms/SelectField/SelectField";
import PromoCodeIcon from "../../../../../../global components/Icons/PromoCode/PromoCode";
import { ThreeDots } from "react-loader-spinner";
import { PaginatedSuccessDataType } from "../../../../../../libs/api/types";

type prop = {
  id: string;
};
type PromoCodeUsage = {
  id: string;
  promoCode: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
    id: string;
  };
  transaction: {
    totalAmount: number;
    quantity: number;
    id: string;
  };
  status: "used" | "unused" | "expired"; // Adjust the literals based on actual possible values
  createdAt: string; // ISO 8601 timestamp
  updatedAt: string; // ISO 8601 timestamp
  __v: number;
};

type PromoCodeUsageResponse = PaginatedSuccessDataType<PromoCodeUsage[]>;

function ViewUsage({ id }: prop) {
  const token = UseAuthToken();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageItems, setPageItems] = useState(50);
  const [filter, setFilter] = useState<string>("");
  const [unauthorised, setUnauthorised] = useState<
    boolean | { title: string; message: string }
  >(false);
  const { data, isLoading, error } = useSWR<PromoCodeUsageResponse, any, any>(
    token
      ? [
          `api/admin/promo-codes/${id}/usages`,
          token,
          { page: currentPage, limit: pageItems, status: filter },
        ]
      : null,
    swrFetcher,
    { revalidateOnFocus: true },
  );
  const columns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (user: string) => <span className={s.credit}>{user}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email: string) => <span className={s.amount}>{email}</span>,
    },
    {
      title: "Total amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (totalAmount: string) => (
        <span className={s.amount}>{totalAmount}</span>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (quantity: string) => (
        <span className={s.credit}>{quantity}</span>
      ),
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <span
          className={clsx(
            s.status,
            s.statusCell,
            s[
              status === "unused"
                ? "pending"
                : status === "used"
                  ? "successful"
                  : "failed"
            ],
          )}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
    },
  ];
  const transformedData = data?.data.map((code) => ({
    key: code.id,
    user: code.user
      ? (code.user.firstName || "") + " " + (code.user.lastName || "")
      : "N/A",
    email: code.user ? code.user.email || "N/a" : "N/A",
    totalAmount: code.transaction.totalAmount
      ? "$" +
        code.transaction.totalAmount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "$" + 0.0,
    quantity: code.transaction.quantity || "N/A",

    status: code.status,
  }));
  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageItems(size);
  };
  function handleFilter(value: string) {
    setFilter(value);
  }
  useEffect(() => {
    if (error) {
      const e = parseClientError(error);
      if (e.statusCode === 403) {
        setUnauthorised(true);
      } else {
        setUnauthorised({
          title: "Error",
          message:
            e.message.charAt(0).toUpperCase() + e.message.slice(1) ||
            "Something went wrong",
        });
      }
    }
  }, [error]);
  return isLoading || error ? (
    <div className={s.spin}>
      <ThreeDots
        visible={true}
        height="50"
        width="50"
        color="#d52b1e"
        radius="9"
        ariaLabel="three-dots-loading"
        wrapperStyle={{
          dispaly: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
        wrapperClass=""
      />
    </div>
  ) : (
    <div className={s.wrapper}>
      <div className={s.table}>
        <div className={s.searchParams}>
          <div className={s.params}>
            <SelectField
              bordered
              label={"Status:"}
              wrapperClassname={s.SelectWrapper}
              defaultValue={""}
              onChange={(value: any) => handleFilter(value)}
              value={filter}
              style={{ width: "100%", height: "100%" }}
              options={[
                { value: "", label: "All" }, // Option to reset the filter
                { value: "expired", label: "Expired" },
                { value: "used", label: "Used" },
                { value: "unused", label: "Unused" },
              ]}
            />
          </div>
          <div className={s.paginate}>
            <Paginate
              style={{ width: "100%" }}
              onChange={handlePageChange}
              total={data?.pagination.totalItems || 0}
              pageSize={pageItems}
            />
          </div>
        </div>

        <ReusableTable
          loading={isLoading}
          noDataComponent={
            unauthorised ? (
              typeof unauthorised === "boolean" ? (
                <Unauthorised />
              ) : (
                <Unauthorised error={unauthorised} />
              )
            ) : (
              <NoData
                Message="No user found"
                subMessage="Get started by adding recipients"
                icon={<PromoCodeIcon height={80} width={80} />}
              />
            )
          }
          dataSource={transformedData || []}
          columns={columns}
        />
      </div>
    </div>
  );
}

export default ViewUsage;
